import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client.safe';
import { useDevMode } from '@/hooks/useDevMode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Activity, Clock, BarChart3, Users, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';

interface TesterSummary {
  user_id: string;
  full_name: string | null;
  email: string | null;
  organization_name: string | null;
  is_currently_active: boolean;
  total_sessions: number;
  total_minutes: number;
  total_revisions: number;
  last_active_at: string | null;
}

interface TesterSession {
  id: string;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number;
}

type TimePeriod = 'week' | 'month' | 'all';

export default function AdminTesterActivityPage() {
  const navigate = useNavigate();
  const { effectiveRole, isSimulating, loading: roleLoading } = useDevMode();
  
  const [testers, setTesters] = useState<TesterSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('week');
  const [expandedTester, setExpandedTester] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Record<string, TesterSession[]>>({});
  const [loadingSessions, setLoadingSessions] = useState<string | null>(null);

  // Check access
  useEffect(() => {
    if (roleLoading) return;
    if (effectiveRole !== 'system_admin') {
      navigate('/');
    }
  }, [effectiveRole, roleLoading, navigate]);

  // Fetch testers
  const fetchTesters = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tester_activity_summary')
        .select('*')
        .order('total_minutes', { ascending: false });

      if (error) throw error;
      setTesters(data || []);
    } catch (error) {
      console.error('Error fetching testers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTesters();
  }, []);

  // Fetch sessions for a specific tester
  const fetchSessions = async (userId: string) => {
    if (sessions[userId]) {
      // Already loaded
      return;
    }

    setLoadingSessions(userId);
    try {
      let query = supabase
        .from('tester_sessions')
        .select('id, started_at, ended_at')
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(50);

      // Add time filter
      if (timePeriod !== 'all') {
        const now = new Date();
        const startDate = new Date();
        if (timePeriod === 'week') {
          startDate.setDate(now.getDate() - 7);
        } else if (timePeriod === 'month') {
          startDate.setMonth(now.getMonth() - 1);
        }
        query = query.gte('started_at', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate duration for each session
      const sessionsWithDuration = (data || []).map(s => {
        const startedAt = new Date(s.started_at);
        const endedAt = s.ended_at ? new Date(s.ended_at) : new Date();
        const durationMinutes = Math.round((endedAt.getTime() - startedAt.getTime()) / (1000 * 60));
        return {
          ...s,
          duration_minutes: durationMinutes
        };
      });

      setSessions(prev => ({ ...prev, [userId]: sessionsWithDuration }));
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(null);
    }
  };

  const toggleExpanded = async (userId: string) => {
    if (expandedTester === userId) {
      setExpandedTester(null);
    } else {
      setExpandedTester(userId);
      await fetchSessions(userId);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just nu';
    if (diffHours < 24) return `${diffHours}h sedan`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d sedan`;
    return date.toLocaleDateString('sv-SE');
  };

  // Calculate stats for the selected time period
  const getStats = () => {
    const now = new Date();
    const startDate = new Date();
    
    if (timePeriod === 'week') {
      startDate.setDate(now.getDate() - 7);
    } else if (timePeriod === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate.setFullYear(2000); // All time
    }

    const activeTesters = testers.filter(t => 
      t.last_active_at && new Date(t.last_active_at) >= startDate
    );

    const totalMinutes = testers.reduce((sum, t) => sum + (t.total_minutes || 0), 0);
    const totalAnalyses = testers.reduce((sum, t) => sum + (t.total_revisions || 0), 0);
    const currentlyActive = testers.filter(t => t.is_currently_active).length;

    return { activeTesters: activeTesters.length, totalMinutes, totalAnalyses, currentlyActive };
  };

  const stats = getStats();

  if (roleLoading || effectiveRole !== 'system_admin') {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tester-aktivitet</h1>
          <p className="text-gray-600">Övervaka studenters användning och engagemang</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriod)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Senaste veckan</SelectItem>
              <SelectItem value="month">Senaste månaden</SelectItem>
              <SelectItem value="all">All tid</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchTesters} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Aktiva just nu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.currentlyActive}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Aktiva testare
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeTesters}</div>
            <p className="text-xs text-gray-500">
              {timePeriod === 'week' ? 'senaste veckan' : 
               timePeriod === 'month' ? 'senaste månaden' : 'totalt'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Total inloggad tid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatDuration(stats.totalMinutes)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Körda analyser
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.totalAnalyses}</div>
          </CardContent>
        </Card>
      </div>

      {/* Activity table */}
      <Card>
        <CardHeader>
          <CardTitle>Detaljerad aktivitet</CardTitle>
          <CardDescription>
            Klicka på en rad för att se sessionshistorik
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Laddar...</div>
          ) : testers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Inga testare registrerade ännu.
            </div>
          ) : (
            <div className="space-y-1">
              {testers.map((tester) => (
                <Collapsible
                  key={tester.user_id}
                  open={expandedTester === tester.user_id}
                  onOpenChange={() => toggleExpanded(tester.user_id)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer border">
                      <div className="flex items-center gap-4">
                        <div className="text-gray-400">
                          {expandedTester === tester.user_id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                        
                        <div>
                          <div className="font-medium">{tester.full_name || tester.email}</div>
                          <div className="text-sm text-gray-500">{tester.organization_name}</div>
                        </div>
                        
                        {tester.is_currently_active && (
                          <Badge className="bg-green-100 text-green-800">Online</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-8 text-sm">
                        <div className="text-center">
                          <div className="font-medium">{tester.total_sessions}</div>
                          <div className="text-gray-500">sessioner</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">{formatDuration(tester.total_minutes)}</div>
                          <div className="text-gray-500">total tid</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">{tester.total_revisions}</div>
                          <div className="text-gray-500">analyser</div>
                        </div>
                        <div className="text-center w-24">
                          <div className="font-medium">{formatRelativeTime(tester.last_active_at)}</div>
                          <div className="text-gray-500">senast aktiv</div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="ml-8 mt-2 mb-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-sm text-gray-700 mb-3">Sessionshistorik</h4>
                      
                      {loadingSessions === tester.user_id ? (
                        <div className="text-sm text-gray-500">Laddar sessioner...</div>
                      ) : sessions[tester.user_id]?.length === 0 ? (
                        <div className="text-sm text-gray-500">Inga sessioner hittade</div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Startad</TableHead>
                              <TableHead>Avslutad</TableHead>
                              <TableHead className="text-right">Längd</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sessions[tester.user_id]?.map((session) => (
                              <TableRow key={session.id}>
                                <TableCell>{formatDate(session.started_at)}</TableCell>
                                <TableCell>
                                  {session.ended_at ? formatDate(session.ended_at) : (
                                    <Badge className="bg-green-100 text-green-800">Pågår</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatDuration(session.duration_minutes)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
